import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Lock, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      const user = await login(email, password);
      toast.success(`${t("login.welcome")}, ${user.name}`);
      navigate("/admin", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#050505] text-white" data-testid="login-page">
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-black">
        <img loading="lazy" decoding="async"           src="https://customer-assets.emergentagent.com/job_soho69-events/artifacts/zk7tuj6l_IMG_1952.jpg"
          alt="Soho69 neon logo"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-black/60" />
        <div className="relative z-10 p-16 flex flex-col justify-between w-full">
          <Link to="/" className="font-heading text-3xl tracking-widest">
            <span className="text-white">SOHO</span>
            <span className="text-neon">69</span>
          </Link>
          <div>
            <h2 className="font-heading text-6xl tracking-tight uppercase max-w-md leading-[0.95]">
              {t("login.side.title.1")} <span className="italic font-display text-neon">{t("login.side.title.2")}</span>
            </h2>
            <p className="text-white/60 mt-4 max-w-sm">{t("login.side.sub")}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6">
          <div>
            <span className="text-[10px] uppercase tracking-[0.32em] text-neon">{t("login.kicker")}</span>
            <h1 className="font-heading text-5xl tracking-tight uppercase mt-2">{t("login.title")}</h1>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.32em] text-neon">{t("login.email")}</span>
            <div className="mt-2 relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="login-email"
                required
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-white/15 pl-10 pr-4 py-3 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon"
                placeholder="taylor@soho69.es"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.32em] text-neon">{t("login.password")}</span>
            <div className="mt-2 relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="login-password"
                required
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-white/15 pl-10 pr-4 py-3 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon"
                placeholder="••••••••"
              />
            </div>
          </label>

          {err && (
            <div data-testid="login-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 px-3 py-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={submitting}
            className="w-full bg-neon text-black py-4 text-xs font-bold uppercase tracking-[0.28em] hover:bg-white disabled:opacity-50 transition"
          >
            {submitting ? t("login.submitting") : t("login.submit")}
          </button>

          <Link
            to="/"
            data-testid="login-back-home"
            className="block text-center text-xs uppercase tracking-[0.28em] text-white/60 hover:text-neon"
          >
            {t("login.back")}
          </Link>
        </form>
      </div>
    </div>
  );
}
